import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ListOrdered,
  ChevronRight,
  Clapperboard,
} from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { Chart } from '@vnetwork/ui/charts';
import { useServiceDetail } from '@/api/media';
import { useVodJobs, useVodStatuses } from '@/api/media/vod-jobs';
import { useJobStats } from '@/api/media/vod-stats';
import { useVodDurationUsage, useVodVolumeUsage, fetchVodUsageCsv } from '@/api/media/vod-usage';
import { useToast } from '@vnetwork/ui/feedback';
import type { VodJob } from '@/api/media/vod-jobs-types';
import { jobStatusTone } from '../../shared/job-status-tone';
import { ChartCard } from '../../_shared/AnalyticsKit';
import { CompactStat, CompactStatGroup } from '../../components/CompactStat';
import { VodConsoleBreadcrumbs } from '../VodConsoleBreadcrumbs';
import styles from './JobsDashboard.module.css';

interface Props {
  serviceId: string;
}

const fmtDate = (raw?: string) => (raw ? new Date(raw).toLocaleString() : '—');

export function VodJobsDashboard({ serviceId }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const base = `/services/vod/${serviceId}`;

  const service = useServiceDetail(serviceId);
  const serviceStatus = (service.data?.status ?? '').toLowerCase();

  const now = new Date();
  const currentYear = now.getFullYear();
  const [durationYear, setDurationYear] = useState(currentYear);
  const [volumeYear, setVolumeYear] = useState(currentYear);
  const [downloadingType, setDownloadingType] = useState<'duration' | 'volume' | null>(null);

  const { data: stats, isPending: statsLoading } = useJobStats(serviceId);
  const { data: recentJobs, isPending: jobsLoading } = useVodJobs(
    serviceId,
    { page: 1, limit: 5 },
  );
  const { data: statuses } = useVodStatuses(serviceId);

  const {
    data: durationUsage = [],
    isPending: durationLoading,
  } = useVodDurationUsage(serviceId, durationYear);

  const {
    data: volumeUsage = [],
    isPending: volumeLoading,
  } = useVodVolumeUsage(serviceId, volumeYear);

  const statusLabelMap = useMemo(
    () =>
      (statuses ?? []).reduce<Record<string, string>>((acc, s) => {
        if (s.value != null) acc[String(s.value)] = s.name;
        return acc;
      }, {}),
    [statuses],
  );

  const yearOptions = [currentYear, currentYear - 1];

  const durationChartData = useMemo(
    () =>
      (durationUsage ?? []).map((d) => ({
        month: d.month,
        total: parseFloat(d.total) || 0,
      })),
    [durationUsage],
  );

  const volumeChartData = useMemo(
    () =>
      (volumeUsage ?? []).map((d) => ({
        month: d.month,
        total: parseFloat(d.total) || 0,
      })),
    [volumeUsage],
  );

  const handleDownload = async (type: 'duration' | 'volume', year: number) => {
    setDownloadingType(type);
    try {
      const blob = await fetchVodUsageCsv(serviceId, type, year);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-usage-${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to download ${type} CSV`);
    } finally {
      setDownloadingType(null);
    }
  };

  const recentColumns = useMemo<ColumnDef<VodJob, unknown>[]>(
    () => [
      {
        id: 'id',
        header: 'Job ID',
        cell: ({ row }) => {
          const id = row.original.id ?? '';
          return <span className={styles.cellMono}>{id.length > 8 ? `${id.slice(0, 8)}…` : id}</span>;
        },
        size: 100,
      },
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className={styles.cellName}>{row.original.name || '—'}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const statusVal = String(row.original.provider_status ?? row.original.status ?? '');
          const label = statusLabelMap[statusVal] || (row.original.status ?? 'Unknown');
          const displayLabel = label.charAt(0).toUpperCase() + label.slice(1);
          return <StatusPill tone={jobStatusTone(label)}>{displayLabel}</StatusPill>;
        },
        size: 140,
      },
      {
        id: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className={styles.cellMuted}>{fmtDate(row.original.created_at)}</span>
        ),
        size: 170,
      },
    ],
    [statusLabelMap],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Clapperboard size={22} />}
        breadcrumbs={<VodConsoleBreadcrumbs />}
        title="Cloud Encoding"
        description={service.data?.service_name || 'Cloud video encoding and packaging with adaptive bitrate.'}
        actions={
          <StatusPill
            tone={serviceStatus === 'active' ? 'success' : service.isLoading ? 'muted' : 'warning'}
            loading={service.isLoading}
          >
            {service.isLoading ? 'Loading' : service.data?.status || 'Unknown'}
          </StatusPill>
        }
      />
      <div className={styles.stack}>
      <ChartCard
        title="Job Summary"
        actions={
          (stats?.failed_job ?? 0) > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              endIcon={<ChevronRight size={14} />}
              onClick={() => navigate(`${base}/jobs`)}
            >
              View failed jobs
            </Button>
          ) : undefined
        }
      >
        {statsLoading ? (
          <Skeleton variant="rect" height={80} />
        ) : (
          <CompactStatGroup columns={5}>
            <CompactStat label="Total Jobs" value={stats?.total_job?.toLocaleString() ?? '—'} />
            <CompactStat label="Completed" value={stats?.completed_job?.toLocaleString() ?? '—'} />
            <CompactStat label="In Progress" value={((stats?.transcoding_job ?? 0) + (stats?.downloading_job ?? 0)).toLocaleString()} />
            <CompactStat label="Pending" value={stats?.pending_job?.toLocaleString() ?? '—'} />
            <CompactStat label="Failed" value={stats?.failed_job?.toLocaleString() ?? '—'} />
          </CompactStatGroup>
        )}
      </ChartCard>

      <div className={styles.chartsRow}>
        <ChartCard
          title="Duration Usage"
          description="Monthly transcoding duration (hours)"
          actions={
            <div className={styles.chartActions}>
              <Select value={String(durationYear)} onValueChange={(v) => setDurationYear(Number(v))}>
                <SelectTrigger size="sm" className={styles.yearSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="secondary"
                size="sm"
                disabled={downloadingType === 'duration'}
                onClick={() => void handleDownload('duration', durationYear)}
              >
                {downloadingType === 'duration' ? 'Downloading…' : 'Export CSV'}
              </Button>
            </div>
          }
        >
          {durationLoading ? (
            <Skeleton variant="rect" height={200} />
          ) : durationChartData.every((d) => !d.total) ? (
            <EmptyState icon={<LayoutDashboard size={20} />} title="No duration data" description="No encoding duration data available for the selected year." size="sm" />
          ) : (
            <Chart
              type="bar"
              data={durationChartData}
              xKey="month"
              series={[{ key: 'total', label: 'Hours' }]}
              height={200}
              valueFormatter={(v) => `${Number(v).toFixed(1)}h`}
            />
          )}
        </ChartCard>

        <ChartCard
          title="Volume Usage"
          description="Monthly output volume (GB)"
          actions={
            <div className={styles.chartActions}>
              <Select value={String(volumeYear)} onValueChange={(v) => setVolumeYear(Number(v))}>
                <SelectTrigger size="sm" className={styles.yearSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="secondary"
                size="sm"
                disabled={downloadingType === 'volume'}
                onClick={() => void handleDownload('volume', volumeYear)}
              >
                {downloadingType === 'volume' ? 'Downloading…' : 'Export CSV'}
              </Button>
            </div>
          }
        >
          {volumeLoading ? (
            <Skeleton variant="rect" height={200} />
          ) : volumeChartData.every((d) => !d.total) ? (
            <EmptyState icon={<LayoutDashboard size={20} />} title="No volume data" description="No output volume data available for the selected year." size="sm" />
          ) : (
            <Chart
              type="bar"
              data={volumeChartData}
              xKey="month"
              series={[{ key: 'total', label: 'GB' }]}
              height={200}
              valueFormatter={(v) => `${Number(v).toFixed(2)} GB`}
            />
          )}
        </ChartCard>
      </div>

      <Section
        title="Recent Jobs"
        description="Latest transcoding tasks"
        style={{ marginBottom: 0 }}
        actions={
          <Button
            variant="ghost"
            size="sm"
            endIcon={<ChevronRight size={14} />}
            onClick={() => navigate(`${base}/jobs`)}
          >
            View all
          </Button>
        }
      >
        {jobsLoading ? (
          <div className={styles.skeletonList}>
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
          </div>
        ) : (
          <DataTable
            columns={recentColumns}
            data={recentJobs?.jobs ?? []}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(`${base}/jobs/${r.id}`)}
            emptyState={
              // Stats reporting jobs while the list is empty signals a transient
              // load failure rather than a genuinely empty account.
              (stats?.total_job ?? 0) > 0 ? (
                <EmptyState
                  icon={<ListOrdered size={20} />}
                  title="Couldn't load recent jobs"
                  description={`Job stats report ${(stats?.total_job ?? 0).toLocaleString()} jobs — the list may be temporarily unavailable.`}
                  size="sm"
                />
              ) : (
                <EmptyState icon={<ListOrdered size={20} />} title="No jobs yet" description="Create your first encoding job." size="sm" />
              )
            }
          />
        )}
      </Section>
      </div>
    </Container>
  );
}

export default VodJobsDashboard;
